/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/QueryModifierAPI',
	'./DataQueryUtils',
	'../../dashboard/queryService/QueryServiceUtils',
	'../../../filters/FilterQuerySpec'
], (_, APIFactory, QueryModifierAPI, DataQueryUtils, QueryServiceUtils, FilterQuerySpec) => {

	/** Query request option to get used parameters in query response. Always send query with this option to get latest parameter info for next query */
	const QFB_USED_PARAMETER_VALUE = {
		qfb: 'usedParameterValue'
	};
	class QueryModifier {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			this.globalFilters = this.dashboard.getFeature('GlobalFilters');
			this.queryExecution = features['DataQueryExecution'];
			this.queryExecution.registerQueryModifier(this.getAPI());
			this.visualization = features['Visualization'];
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [QueryModifierAPI]);
			}
			return this.api;
		}

		getType() {
			return 'main';
		}

		modifyQuerySpecListForQueryHint(querySpecList) {
			//add more queryHints
			querySpecList.forEach(querySpec=>{
				if (querySpec.type === this.getType()) {
					//we only add to main query
					this._addQueryHintToSpec(querySpec.spec);
				}
			});
			return querySpecList;
		}

		modifyQuerySpecList(querySpecList) {
			querySpecList = this.modifyQuerySpecListForFilters(querySpecList);
			querySpecList = this.modifyQuerySpecListForQueryHint(querySpecList);
			querySpecList = this.modifyQuerySpecListForPrompts(querySpecList);
			this.queryExecution.addRequestOptions(QFB_USED_PARAMETER_VALUE);
			return querySpecList;
		}

		modifyQuerySpecListForFilters(querySpecList) {
			const filterSpecList = DataQueryUtils.getFilterSpecListByVisualization(this.content, this.dashboard);
			const updatedFilterSpecList = DataQueryUtils.convertExcludeEmptyValuesFilters(filterSpecList, this.visualization.getDataSource());
			const filterQuerySpecList = this._getFilterQuerySpecList(updatedFilterSpecList);

			// apply the global filter to each query spec
			if (filterQuerySpecList && filterQuerySpecList.length) {
				_.each(querySpecList, (querySpec) => {
					if (querySpec.type === this.getType()) {
						if (!querySpec.spec.filters) {
							querySpec.spec.filters = [];
						}
						querySpec.spec.filters.push(...filterQuerySpecList);
					}
				});
			}
			return querySpecList;
		}

		/**
		 * @private
		 * Get the list of global filter query specifications
		 */
		_getFilterQuerySpecList(filterList) {
			const filterSpec = new FilterQuerySpec(this.visualization);
			filterSpec.addFiltersToSpec(filterList);
			return filterSpec.hasFilterSpec() ? filterSpec.getFilterSpec() : [];
		}

		_addValueToQueryHints(spec, queryHintKey, queryHintValue) {
			if (!spec.queryHints) {
				spec.queryHints = {};
			}
			spec.queryHints[queryHintKey] = queryHintValue;
		}

		_addQueryHintToSpec(spec) {
			const preferredModelItems = DataQueryUtils.getRelatedModelItemsForQueryHint(this.content, this.dashboard);
			if (preferredModelItems.length) {
				this._addValueToQueryHints(spec, 'preferredModelItems', preferredModelItems);
			}

			const refreshProperty = this.content.getPropertyValue('queryRefresh');
			if (!(refreshProperty && refreshProperty.autoRefresh)) {
				const localCache = this.dashboard.getFeature('Canvas').getPropertyValue('dataCache');
				if (localCache === 'yes') {
					this._addValueToQueryHints(spec, 'dataCacheExpiry', '3600');
				} else if (localCache === 'no') {
					this._addValueToQueryHints(spec, 'dataCacheExpiry', '0');
				}
			}
		}

		/**
		 * This function is in parallel with {@link QueryDefinitionModifier#_getSavedParameterValues}
		 */
		modifyQuerySpecListForPrompts(queryList) {
			const savedPrompts = this.visualization.getSavedPrompts();
			const promptSpecList =  savedPrompts.getPromptSpecList();
			if (promptSpecList && promptSpecList.length) {
				_.each(promptSpecList, promptSpec => {
					if (!promptSpec.capabilities.optional) {
						// MUN based dataType expect a useValue while all other types expect a displayValue for a parameter.
						promptSpec.values = QueryServiceUtils.getPromptValues(promptSpec.values, promptSpec.dataType);
					}
				});
				queryList.forEach(query => query.spec.parameterValues = promptSpecList);
			}
			return queryList;
		}

	}
	return QueryModifier;
});
